import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Input } from '../components/ui'
import { signIn, requestMagicLink } from '../lib/auth-client'

type LoginMode = 'password' | 'magic-link'

export function Login() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<LoginMode>('magic-link')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [magicLinkSent, setMagicLinkSent] = useState(false)

  const validateEmail = (value: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const trimmedEmail = email.trim()

    if (!trimmedEmail) {
      setError('Email is required')
      return
    }
    if (!validateEmail(trimmedEmail)) {
      setError('Please enter a valid email address')
      return
    }
    if (!password) {
      setError('Password is required')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setIsLoading(true)
    try {
      await signIn(trimmedEmail, password)
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      navigate('/')
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('Failed to fetch') || err.message.includes('network')) {
          setError('Connection failed. Please check your internet connection.')
        } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
          setError('Invalid email or password')
        } else if (err.message.includes('429')) {
          setError('Too many attempts. Please try again later.')
        } else {
          setError('An error occurred. Please try again.')
        }
      } else {
        setError('An unexpected error occurred')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const trimmedEmail = email.trim()

    if (!trimmedEmail) {
      setError('Email is required')
      return
    }
    if (!validateEmail(trimmedEmail)) {
      setError('Please enter a valid email address')
      return
    }

    setIsLoading(true)
    try {
      await requestMagicLink(trimmedEmail, '/')
      setMagicLinkSent(true)
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('429')) {
          setError('Too many attempts. Please try again later.')
        } else {
          setError('An error occurred. Please try again.')
        }
      } else {
        setError('An unexpected error occurred')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-swiss-white">
      <div className="w-full max-w-[320px] px-4">
        {/* Logo and Title */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-8 h-8 bg-swiss-black flex items-center justify-center">
            <span className="text-swiss-white font-bold text-[14px]">D</span>
          </div>
          <span className="text-[18px] font-medium text-swiss-black">
            Derivative Admin
          </span>
        </div>

        <h1 className="text-[11px] uppercase tracking-[0.1em] font-medium text-swiss-gray-500 mb-6">
          Sign In
        </h1>

        {error && (
          <div
            role="alert"
            aria-live="polite"
            className="mb-4 p-3 border-l-2 border-swiss-red bg-swiss-off-white"
          >
            <p className="text-[13px] text-swiss-red">{error}</p>
          </div>
        )}

        {magicLinkSent ? (
          <div className="p-4 border border-swiss-gray-200 bg-swiss-off-white">
            <p className="text-[13px] text-swiss-black">
              Check your email — we sent a sign-in link to <strong>{email.trim()}</strong>.
            </p>
            <p className="text-[12px] text-swiss-gray-500 mt-2">
              Click the link in the email to sign in. The link expires in 15 minutes.
            </p>
            <button
              type="button"
              onClick={() => { setMagicLinkSent(false); setEmail('') }}
              className="mt-4 text-[13px] text-swiss-black underline hover:no-underline"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <>
            {/* Mode toggle */}
            <div className="flex border-b border-swiss-gray-200 mb-4">
              <button
                type="button"
                onClick={() => setMode('magic-link')}
                className={`flex-1 py-2 text-[13px] font-medium ${
                  mode === 'magic-link'
                    ? 'text-swiss-black border-b-2 border-swiss-black -mb-[2px]'
                    : 'text-swiss-gray-500'
                }`}
              >
                Magic link
              </button>
              <button
                type="button"
                onClick={() => setMode('password')}
                className={`flex-1 py-2 text-[13px] font-medium ${
                  mode === 'password'
                    ? 'text-swiss-black border-b-2 border-swiss-black -mb-[2px]'
                    : 'text-swiss-gray-500'
                }`}
              >
                Password
              </button>
            </div>

            {mode === 'magic-link' ? (
              <form onSubmit={handleMagicLinkSubmit} className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  disabled={isLoading}
                />
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending link...' : 'Email me a sign-in link'}
                </Button>
                <p className="text-[12px] text-swiss-gray-500">
                  We’ll send a link to your email — no password needed. You must have an account already.
                </p>
              </form>
            ) : (
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  disabled={isLoading}
                />
                <Input
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  disabled={isLoading}
                />
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Login
